import 'leaflet/dist/leaflet.css'
import React from "react"
import L from "leaflet"
import { ArrowLeft16Regular, ArrowRight16Regular } from "@fluentui/react-icons"
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet"
import './assets/sideMap.scss'

interface SideMapPoint {
    id: string
    lat: number
    lng: number
    title: string
    pointType: 'site' | 'resource'
    color: string
    initials: string
    subtitle?: string
    detail?: string
}

interface SideMapProps {
    isOpen: boolean
    onToggle: () => void
    onLayoutSettled?: () => void
    points: SideMapPoint[]
    fallbackCenter?: {
        latitude: number | null
        longitude: number | null
    }
}

const FALLBACK_CENTER: [number, number] = [-33.8688, 151.2093] // Sydney
const DEFAULT_ZOOM = 11
const markerIconCache = new Map<string, L.DivIcon>()

const escapeHtml = (value: string): string => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const getMapPointMarkerIcon = (color: string, initials: string): L.DivIcon => {
    const cacheKey = `${color}|${initials}`;
    const existingIcon = markerIconCache.get(cacheKey);
    if (existingIcon) {
        return existingIcon;
    }

    const markerIcon = L.divIcon({
        className: 'rb-side-map-marker-icon',
        html: `
            <div class="rb-side-map-marker" style="--rb-marker-color: ${escapeHtml(color)};">
                <span>${escapeHtml(initials)}</span>
            </div>
        `,
        iconSize: [34, 34],
        iconAnchor: [17, 34],
        popupAnchor: [0, -30]
    });

    markerIconCache.set(cacheKey, markerIcon);
    return markerIcon;
}

type RenderedSideMapPoint = SideMapPoint & {
    markerLat: number
    markerLng: number
}

const spreadOverlappingPoints = (points: SideMapPoint[]): RenderedSideMapPoint[] => {
    const groupedByCoordinate = new Map<string, SideMapPoint[]>();

    points.forEach((point) => {
        const key = `${point.lat.toFixed(6)}:${point.lng.toFixed(6)}`;
        const existing = groupedByCoordinate.get(key);
        if (existing) {
            existing.push(point);
            return;
        }
        groupedByCoordinate.set(key, [point]);
    });

    const renderedPoints: RenderedSideMapPoint[] = [];
    groupedByCoordinate.forEach((group) => {
        const orderedGroup = [...group].sort((left, right) => {
            if (left.pointType === 'site' && right.pointType !== 'site') return -1;
            if (left.pointType !== 'site' && right.pointType === 'site') return 1;
            return left.title.localeCompare(right.title);
        });

        const anchor = orderedGroup[0];
        const siteAnchored = orderedGroup[0]?.pointType === 'site';
        const orbitCount = siteAnchored ? orderedGroup.length - 1 : orderedGroup.length;

        orderedGroup.forEach((point, index) => {
            if (orderedGroup.length === 1 || (siteAnchored && index === 0)) {
                renderedPoints.push({
                    ...point,
                    markerLat: point.lat,
                    markerLng: point.lng
                });
                return;
            }

            const orbitIndex = siteAnchored ? index - 1 : index;
            const ringIndex = Math.floor(orbitIndex / 6);
            const radiusMeters = 42 + (ringIndex * 18);
            const angle = (2 * Math.PI * orbitIndex) / Math.max(orbitCount, 1);
            const latitudeOffset = (radiusMeters / 111320) * Math.sin(angle);
            const cosLatitude = Math.max(Math.cos((anchor.lat * Math.PI) / 180), 0.2);
            const longitudeOffset = (radiusMeters / (111320 * cosLatitude)) * Math.cos(angle);

            renderedPoints.push({
                ...point,
                markerLat: point.lat + latitudeOffset,
                markerLng: point.lng + longitudeOffset
            });
        });
    });

    return renderedPoints;
}

const fitMapViewport = (
    map: L.Map,
    points: RenderedSideMapPoint[],
    fallbackCenter?: SideMapProps['fallbackCenter']
) => {
    if (points.length > 0) {
        const bounds = new L.LatLngBounds(points.map(({ markerLat, markerLng }) => [markerLat, markerLng]));
        if (!bounds.isValid()) {
            return;
        }

        if (points.length === 1) {
            map.setView(bounds.getCenter(), 13, { animate: false });
            return;
        }

        map.fitBounds(bounds, {
            padding: [36, 36],
            maxZoom: 14,
            animate: false
        });
        return;
    }

    const latitude = fallbackCenter?.latitude;
    const longitude = fallbackCenter?.longitude;
    if (Number.isFinite(latitude ?? NaN) && Number.isFinite(longitude ?? NaN)) {
        map.setView([latitude as number, longitude as number], DEFAULT_ZOOM, { animate: false });
    } else {
        map.setView(FALLBACK_CENTER, DEFAULT_ZOOM, { animate: false });
    }
};

const MapViewportController = ({ points, fallbackCenter }: { points: RenderedSideMapPoint[]; fallbackCenter?: SideMapProps['fallbackCenter'] }) => {
    const map = useMap();

    React.useEffect(() => {
        const updateViewport = () => {
            map.invalidateSize({ pan: false });
            fitMapViewport(map, points, fallbackCenter);
        };

        const scheduleViewportUpdate = () => {
            requestAnimationFrame(updateViewport);
        };

        const container = map.getContainer();
        scheduleViewportUpdate();

        const timeoutIds = [
            window.setTimeout(scheduleViewportUpdate, 0),
            window.setTimeout(scheduleViewportUpdate, 180),
            window.setTimeout(scheduleViewportUpdate, 360)
        ];

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                scheduleViewportUpdate();
            });
            resizeObserver.observe(container);
        }

        window.addEventListener('resize', scheduleViewportUpdate);

        return () => {
            timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
            resizeObserver?.disconnect();
            window.removeEventListener('resize', scheduleViewportUpdate);
        };
    }, [map, points, fallbackCenter?.latitude, fallbackCenter?.longitude]);

    return null;
}

const SideMap = ({ isOpen, onToggle, onLayoutSettled, points, fallbackCenter }: SideMapProps) => {
    const renderedPoints = React.useMemo(() => spreadOverlappingPoints(points), [points]);
    const hasPoints = points.length > 0
    const siteCount = points.filter((point) => point.pointType === 'site').length
    const resourceCount = points.filter((point) => point.pointType === 'resource').length
    const fallbackLatitude = fallbackCenter?.latitude
    const fallbackLongitude = fallbackCenter?.longitude
    const isFallbackCenterValid = Number.isFinite(fallbackLatitude ?? NaN) && Number.isFinite(fallbackLongitude ?? NaN)
    const initialCenter: [number, number] = isFallbackCenterValid
        ? [fallbackLatitude as number, fallbackLongitude as number]
        : FALLBACK_CENTER

    return (
        <aside
            className={`rb-side-map-drawer ${isOpen ? 'is-open' : 'is-closed'}`}
            onTransitionEnd={(event) => {
                if (event.target !== event.currentTarget || event.propertyName !== 'width') {
                    return
                }

                onLayoutSettled?.()
            }}
        >
            <button
                type="button"
                className="rb-side-map-toggle"
                onClick={onToggle}
                aria-label={isOpen ? 'Hide shift map' : 'Show shift map'}
                title={isOpen ? 'Hide shift map' : 'Show shift map'}
            >
                {isOpen ? <ArrowRight16Regular /> : <ArrowLeft16Regular />}
            </button>

            {isOpen ? (
                <section className="rb-side-map-panel">
                    <div className="rb-side-map-header">
                        <span>Roster Locations</span>
                        <span>{siteCount} site, {resourceCount} resource{resourceCount === 1 ? '' : 's'}</span>
                    </div>
                    <div className="rb-side-map-container">
                        <MapContainer center={initialCenter} zoom={DEFAULT_ZOOM} scrollWheelZoom={true} className="rb-side-map">
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            {hasPoints && renderedPoints.map((point) => (
                                <Marker
                                    key={point.id}
                                    position={[point.markerLat, point.markerLng]}
                                    icon={getMapPointMarkerIcon(point.color, point.initials)}
                                >
                                    <Popup>
                                        <div className="rb-side-map-popup-content">
                                            <div className="rb-side-map-popup-title">{point.title}</div>
                                            {point.subtitle ? (
                                                <div className="rb-side-map-popup-subtitle">
                                                    <span className="rb-side-map-popup-swatch" style={{ backgroundColor: point.color }} />
                                                    <span>{point.subtitle}</span>
                                                </div>
                                            ) : null}
                                            {point.detail ? (
                                                <div className="rb-side-map-popup-detail">{point.detail}</div>
                                            ) : null}
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                            <MapViewportController points={renderedPoints} fallbackCenter={fallbackCenter} />
                        </MapContainer>
                        {!hasPoints ? (
                            <div className="rb-side-map-empty">No site or resource locations with coordinates are available in the current rosterboard view.</div>
                        ) : null}
                    </div>
                </section>
            ) : null}
        </aside>
    )
}

export { SideMap }
export type { SideMapPoint }
